/* @flow */
import BoxBody from 'components/BoxBody.jsx';
import Button from 'components/Button.jsx';
import CurrentUser from 'auth/CurrentUser';
import { debounce } from 'lodash';
import Dialog from 'components/Dialog.jsx';
import Divider from 'components/Divider.jsx';
import Group from 'entities/Group';
import GroupActions from 'groups/actions/GroupActions';
import GroupAvatarSelector from './GroupAvatarSelector.jsx';
import GroupForm from './GroupForm.jsx';
import GroupOptionsForm from './GroupOptionsForm.jsx';
import MediaObject from 'components/MediaObject.jsx';
import * as React from 'react';
import SetUserEmailModal from 'settings/components/SetUserEmailModal.jsx';
import ToastActions from 'actions/ToastActions';
import User from 'entities/User';
import Organization from 'entities/Organization';
import OrganizationStore from 'stores/OrganizationStore';
import { Container } from 'flux/utils';
import errorLogger from 'utils/errorLogger';
import Row from 'components/Row.jsx';

import './styles/GroupCreateModal.scss';

type Props = {
  onCreate?: ?(group: Group) => void,
  onRequestHide: () => void,
};

type State = {
  hasChildren: boolean,
  code: ?string,
  name: ?string,
  user: ?User,
  avatarHandpicked: boolean,
  groupCodeHandpicked: boolean,
  suggestedGroupCode: boolean,
  canSubmit: boolean,
  initialOrganization: ?Organization,
};

const makeInitialState = function(): State {
  return {
    hasChildren: false,
    code: null,
    name: null,
    user: null,
    avatarHandpicked: false,
    groupCodeHandpicked: false,
    suggestedGroupCode: false,
    canSubmit: false,
    initialOrganization: null,
  };
};

class GroupCreateModal extends React.Component<Props, State> {
  static getStores() {
    return [CurrentUser, OrganizationStore];
  }

  static calculateState(prevState: ?State) {
    const user = CurrentUser.isSignedIn() ? CurrentUser.getUser() : null;
    const orgId = user ? user.getOrganizationId() : null;
    return {
      ...(prevState || makeInitialState()),
      user,
      initialOrganization: orgId ? OrganizationStore.getOrganization(orgId) : null,
    };
  }

  suggestGroupCode = debounce(name => {
    Group.suggestCode(name)
      .then(codes => {
        if (!this.state.code && codes && codes[0]) {
          this.setState({ code: codes[0] });
          this.refs.groupForm.changeCodeTo(codes[0]);
        }
        this.setState({ suggestedGroupCode: true });
      })
      .catch(errorLogger('GroupCreateModal suggestCode failed'));
  }, 300);
  suggestAvatarForName = debounce(name => {
    if (!this || !this.refs || !this.refs.avatarSelector) {
      return;
    }
    this.refs.avatarSelector.searchAvatarForName(name);
  }, 300);
  onAvatarSelected = () => {
    this.setState({ avatarHandpicked: true });
  };
  onCodeChange = () => {
    if (!this.state.groupCodeHandpicked) {
      this.setState({ groupCodeHandpicked: true });
    }
  };
  onOptionsChange = (name, value) => {
    this.setState({ [name]: value });
  };
  onSubmit = () => {
    const orgData = {
      organization: this.refs.groupForm.getOrganizationForSubmit(),
    };

    const groupToCreate = new Group(
      Object.assign(
        {
          name: this.state.code,
          class_name: this.state.name,
          group_avatar: {
            id: this.refs.avatarSelector.getSelectedAvatarId(),
          },
        },
        this.refs.options.getValues(),
        orgData
      )
    );

    GroupActions.create(groupToCreate)
      .then(group => {
        this.props.onRequestHide();
        if (this.props.onCreate) {
          this.props.onCreate(group);
        }
      })
      .catch(ToastActions.showAPIError);
  };
  onFormInvalid = () => {
    // Prevent excessive re-renders
    if (this.state.canSubmit) {
      this.setState({ name: null, code: null, canSubmit: false });
    }
  };
  onFormValid = (name, code) => {
    this.setState({ name, code, canSubmit: true });
  };
  onValidNameChange = name => {
    if (!this.state.groupCodeHandpicked && !this.state.suggestedGroupCode) {
      this.suggestGroupCode(name);
    }

    if (!this.state.avatarHandpicked) {
      this.suggestAvatarForName(name);
    }
  };

  render() {
    const { user } = this.state;
    if (user && !user.hasEmail()) {
      return (
        <SetUserEmailModal
          {...this.props}
          closeOnSave={false}
          message={'An email address is required to create a group.'}
        />
      );
    }

    return (
      <Dialog
        eventScreenName="GroupCreateModal"
        {...this.props}
        className="GroupCreateModal"
        title="Create a class"
        size="small">
        <BoxBody divided>
          <MediaObject className="GroupCreateModal-top">
            <div>
              <GroupAvatarSelector ref="avatarSelector" onSelect={this.onAvatarSelected} />
            </div>
            <div>
              <GroupForm
                ref="groupForm"
                onFormValid={this.onFormValid}
                onValidNameChange={this.onValidNameChange}
                onCodeChange={this.onCodeChange}
                onFormInvalid={this.onFormInvalid}
                hasChildren={this.state.hasChildren}
                showOrg
                currentOrganization={this.state.initialOrganization}
              />
            </div>
          </MediaObject>
          <Divider size="medium" />
          <Row>
            <GroupOptionsForm
              ref="options"
              subscriberInitiatedChats
              hasChildren={this.state.hasChildren}
              showSearchable={false}
              userIsTeacher={CurrentUser.getUser().isTeacher()}
              onChange={this.onOptionsChange}
            />
          </Row>
          <Button
            className="GroupCreateModal-submitButton"
            disabled={!this.state.canSubmit}
            onClick={this.onSubmit}
            data-test-selector="group-create-modal-submit">
            Create
          </Button>
        </BoxBody>
      </Dialog>
    );
  }
}

export default Container.create(GroupCreateModal, { withProps: true });
